import { Back, TweenMax } from 'gsap';
import { Point, Sprite } from 'pixi.js';
import { getStarSound } from '../../Config/Audio';
import { getGameSize } from '../../Config/Levels';
import SoundManager from '../../Sound/SoundManager';
import { waitForMs } from '../../Utils/AsyncUtils';
import HudView from '../../View/HudView';
import MapView from '../../View/MapView';
import { BottomUi } from '../Game/BottomUI/BottomUi';
import { UIButton } from '../Shared/Buttons/UIButton';
import { Sparkle } from './Sparkle';
import { Star } from './Star';
import Core, { GameOrientation } from '../../Core/Core';
import { Popup } from '../../Managers/Popups';

// TODO change button animations to cancelable and cleanup-able classes like the reward animation
/**
 * plays an animation on the selected button after selected time interval
 * @param button: the button where the animation should be played
 * @param interval: the interval till the animation is played in seconds
 */
export const buttonIdleAnimation = (button: UIButton, interval: number) => {
    if (!button.originalScale) {
        button.originalScale = new Point(button.scale.x, button.scale.y);
    }
    // button animation goes here
    TweenMax.to(button.scale, 0.1, {
        delay: 0.7,
        ease: Back.easeOut.config(0.1),
        onComplete: () => {
            // scaleDuration is how long the animation plays out till the next step is started
            TweenMax.to(button.scale, 0.1, {
                ease: Back.easeOut.config(0.1),
                onComplete: () => {
                    TweenMax.to(button.scale, 0.1, {
                        ease: Back.easeOut.config(0.1),
                        onComplete: () => {
                            TweenMax.to(button.scale, 0.2, {
                                ease: Back.easeOut.config(0.1),
                                onComplete: () => {
                                    TweenMax.to(button, 0, {
                                        delay: interval - 0.7,
                                        onComplete: () => {
                                            if (button.parent !== null) {
                                                buttonIdleAnimation(button, interval);
                                            }
                                        },
                                    });
                                },
                                x: 1 * button.originalScale.x,
                                y: 1 * button.originalScale.y,
                            });
                        },
                        x: 1.1 * button.originalScale.x,
                        y: 0.95 * button.originalScale.y,
                    });
                },
                x: 1.1 * button.originalScale.x,
                y: 1.1 * button.originalScale.y,
            });
        },
        x: 0.9 * button.originalScale.x,
        y: 1 * button.originalScale.y,
    });
};

/**
 * adds a small scaling down and up again animation to the button
 * used for pressing a button feedback
 * @param button the button thats used for the animation
 * @param scaling the scaling value
 */
export const buttonPressAnimation = (
    button: PIXI.Container,
    scaling: number,
    skipRelease?: boolean
) => {
    const originalScale = button.scale;
    TweenMax.to(button.scale, 0.1, {
        onComplete: () => {
            if (!skipRelease) {
                buttonReleaseAnimation(button);
            }
        },
        x: scaling * originalScale.x,
        y: scaling * originalScale.y,
    });
};
export const buttonReleaseAnimation = (button: PIXI.Container, scale?: PIXI.Point) => {
    TweenMax.to(button.scale, 0.1, {
        x: scale ? scale.x : 1,
        y: scale ? scale.y : 1,
    });
};

export class AddCurrencyAnimation {
    public animationDuration: number = 0;

    private curQuantity: number;
    private totalQuantity: number;
    private sprites: Sprite[];
    private targetUi: any;
    private startX: number =
        Core.orientation === GameOrientation.PORTRAIT
            ? getGameSize().gameWidth * 0.5
            : -getGameSize().gameWidth * 0.1;
    private startY: number = getGameSize().gameHeight * 0.5;
    private targetX: number = 0;
    private targetY: number = 0;
    private spriteId: string = 'mapui/tophud-icon-heart';
    private currencyId: string;
    private soundId: string = 'GameBooster';
    private waitTime: number = 100;
    private rate: number = 1;

    constructor(quantity: number, addQuantity: number, hudView: HudView, id: string) {
        this.currencyId = id;
        const topMapUi = hudView.mapUi;
        this.spriteId = 'commonui/reward-' + id + (id === 'coin' ? '01' : '');

        const maxQuantity = Math.min(25, quantity);
        this.rate = quantity / maxQuantity;
        if (id === 'heart') {
            this.targetUi = topMapUi.hearts;
            this.waitTime = 400;
        }
        if (id === 'coin') {
            this.targetUi = topMapUi.coins;
            this.waitTime = 10;
            this.soundId = 'GameCoin';
        }

        if (id.indexOf('token') !== -1) {
            this.targetUi = hudView.mapBottomUi.getButton(Popup.JOURNAL);

            const startPosition = new Point(
                getGameSize().gameWidth * 0.5,
                getGameSize().gameHeight * 0.5
            );

            const local = this.targetUi.parent.toLocal(startPosition);
            this.startX = local.x;
            this.startY = local.y;

            this.waitTime = 10;
        }

        if (id === 'star') {
            this.spriteId = 'commonui/top-hud-starsbar-star';
            this.targetUi = topMapUi.stars;
            this.waitTime = 400;
        }

        this.curQuantity = quantity / this.rate;
        this.totalQuantity = addQuantity;
        this.setAnimEndPosition();

        this.sprites = [];
        this.animationDuration = 400 + this.curQuantity * this.waitTime;
    }

    public cleanUp() {
        this.curQuantity = 0;
        this.sprites.forEach(s => {
            TweenMax.killTweensOf(s.scale);
            TweenMax.killTweensOf(s);
            s.destroy();
        });
        this.sprites = [];
    }

    public async showCurrencyAdded() {
        const targetScale = this.targetUi.icon ? this.targetUi.icon.scale : this.targetUi.scale;

        TweenMax.killTweensOf(targetScale);
        TweenMax.to(targetScale, 0.2, {
            x: 1.2,
            y: 1.2,
        });
        while (this.curQuantity > 0) {
            const randomPosition = this.currencyId === 'coin' || this.currencyId.includes('token');
            const s = new Sprite(PIXI.utils.TextureCache[this.spriteId]);

            s.x = randomPosition
                ? this.startX + Math.floor(Math.random() * 100) - 100
                : this.startX;
            s.y = randomPosition
                ? this.startY + Math.floor(Math.random() * 100) - 100
                : this.startY;
            s.anchor.set(0.5);
            s.scale.set(0);

            if (this.targetUi.parent) {
                this.targetUi.parent.addChild(s);
                if (Core.orientation === GameOrientation.LANDSCAPE) {
                    s.rotation = -this.targetUi.parent.rotation;
                }
            }
            this.sprites.push(s);
            this.scaleUp(s);

            await waitForMs(this.waitTime);

            this.curQuantity--;
        }

        TweenMax.to(targetScale, 0.2, {
            delay: 0.5,
            x: 1,
            y: 1,
        });
    }

    private scaleUp(s: Sprite) {
        TweenMax.to(s.scale, 0.2, {
            onComplete: () => {
                this.toUi(s);
            },
            x: 1.5,
            y: 1.5,
        });
    }

    private toUi(s: Sprite) {
        this.setAnimEndPosition();

        TweenMax.to(s.scale, 0.2, {
            delay: 0.5,
            x: 1,
            y: 1,
        });
        TweenMax.to(s, 0.2, {
            delay: 0.5,
            onComplete: () => {
                if (this.targetUi.parent) {
                    this.targetUi.parent.removeChild(s);
                }
                s.destroy();
                this.sprites.splice(this.sprites.indexOf(s), 1);
                this.addStarAnim();

                if (this.targetUi.showCurrencyValue) {
                    this.targetUi.showCurrencyValue(this.totalQuantity);
                }
            },
            onStart: () => {
                if (this.currencyId === 'star') {
                    SoundManager.playSound(getStarSound(4 - (this.curQuantity + 1)));
                } else if (this.sprites.indexOf(s) === 0) {
                    SoundManager.playSound(this.soundId);
                }
            },
            x: this.targetX,
            y: this.targetY,
        });
    }

    private setAnimEndPosition() {
        this.targetX = this.targetUi.x - this.targetUi.width * 0.4;
        this.targetY = this.targetUi.y;

        if (this.currencyId.indexOf('token') !== -1) {
            this.targetX = this.targetUi.x;
        }
    }

    private addStarAnim() {
        const starAnim = this.currencyId === 'star' ? new Star(1.1) : new Sparkle(1.5);
        const starAnimSprite = starAnim.sprite;
        starAnimSprite.x = this.targetX;
        starAnimSprite.y = this.targetY;
        starAnimSprite.rotation = 15 * (Math.PI / 180) * -1;
        if (!this.targetUi.parent) {
            return;
        }
        this.targetUi.parent.addChild(starAnimSprite);
        starAnim.play();
    }
}

/**
 * animation that can be played on the map screen if the player gets a heart
 * @param quantity the number of hearts the player got
 * @param mapUI reference to the topmapui so the animation can be on top of it
 */

export class AddBoosterAnimation {
    private counter: number;
    private targetNode: any;
    private targetX: number;
    private targetY: number;
    private boosterChange: string[];
    private sprites: Sprite[];

    constructor(boosterChange: string[], view: MapView, curLevelNode: number) {
        this.boosterChange = [...new Set(boosterChange)];
        curLevelNode = Math.min(curLevelNode - 1, view.areaMap.nodes.length - 1);
        this.targetNode = curLevelNode > 0 ? view.areaMap.nodes[curLevelNode] : 0;
        this.targetX = this.targetNode.x;
        this.targetY = this.targetNode.y;
        this.counter = this.boosterChange.length - 1;
        this.sprites = [];
    }

    public async showBoostersAdded(view: MapView) {
        while (this.counter >= 0) {
            const s = new Sprite(
                PIXI.utils.TextureCache[
                    'mapui/booster-' + this.boosterChange[this.counter].toLowerCase()
                ]
            );
            s.x = this.targetX;
            s.y = this.targetY - getGameSize().gameHeight * 0.15;
            s.anchor.set(0.5);
            s.scale.set(0);
            view.areaMap.addChild(s);
            this.sprites.push(s);

            this.scaleUp(s);

            await waitForMs(500);
            this.counter--;
        }
    }

    public cleanUp() {
        this.counter = 0;
        this.sprites.forEach(s => {
            TweenMax.killTweensOf(s.scale);
            TweenMax.killTweensOf(s);
            s.destroy();
        });
        this.sprites = [];
    }

    public complete(s: Sprite) {
        const starAnim = new Sparkle(1.5);
        const starAnimSprite = starAnim.sprite;
        starAnimSprite.x = this.targetX;
        starAnimSprite.y = this.targetY;
        starAnimSprite.rotation = 15 * (Math.PI / 180) * -1;
        if (s.parent) {
            s.parent.addChild(starAnimSprite);
            s.parent.removeChild(s);
            s.destroy();
        }

        starAnim.play();
        TweenMax.to(this.targetNode.scale, 0.1, {
            yoyo: true,
            repeat: 1,
            x: 1.2,
            y: 1.2,
        });
    }

    private scaleUp(s: Sprite) {
        TweenMax.to(s.scale, 0.2, {
            onComplete: () => {
                this.toNode(s);
            },
            x: 1.5,
            y: 1.5,
        });
    }

    private toNode(s: Sprite) {
        TweenMax.to(s.scale, 0.2, {
            delay: 0.5,
            x: 1,
            y: 1,
        });
        TweenMax.to(s, 0.2, {
            delay: 0.5,
            onComplete: () => {
                this.complete(s);
            },
            onStart: () => {
                SoundManager.playSound('GameBooster');
            },
            x: this.targetX,
            y: this.targetY,
        });
    }
}

export class AddCoinsAnimationInGame {
    private curQuantity: number;
    private addQuantity: number;
    private curCoins: number;
    private sprites: Sprite[];
    private bottomUi: BottomUi;
    private startX: number =
        Core.orientation === GameOrientation.PORTRAIT
            ? getGameSize().gameWidth * 0.5
            : -getGameSize().gameWidth * 0.1;
    private startY: number =
        Core.orientation === GameOrientation.PORTRAIT ? 0 : getGameSize().gameHeight * 0.5;
    private targetX: number = 0;
    private targetY: number = 0;
    private spriteId: string = 'mapui/tophud-icon-coin';
    private soundId: string = 'GameCoin';
    private waitTime: number = 100;

    constructor(quantity: number, curCoins: number, bottomUi: BottomUi) {
        if (quantity > 100) {
            quantity = 100;
        }

        this.curQuantity = Math.round(quantity / 10);
        if (this.curQuantity === 0 && quantity > 0) {
            this.curQuantity = 1;
        }
        this.addQuantity = quantity;
        this.curCoins = curCoins;
        this.bottomUi = bottomUi;
        this.sprites = [];
        this.bottomUi.wallet.updatedCoinTxt(this.curCoins - this.curQuantity * 10);
        this.targetX = this.bottomUi.boosters.x + this.bottomUi.boosters.width * 0.3;
        this.targetY = this.bottomUi.wallet.y;
    }

    public cleanUp() {
        this.bottomUi.wallet.updatedCoinTxt(this.curCoins);
        this.curQuantity = 0;
        this.sprites.forEach(s => {
            TweenMax.killTweensOf(s.scale);
            TweenMax.killTweensOf(s);
            s.destroy();
        });
        this.sprites = [];
    }

    public async showCurrencyAdded() {
        while (this.curQuantity > 0) {
            const s = new Sprite(PIXI.utils.TextureCache[this.spriteId]);
            s.x = this.startX + Math.floor(Math.random() * 100) - 100;
            s.y = this.startY + Math.floor(Math.random() * 100) - 100;
            s.anchor.set(0.5);
            s.scale.set(0);
            this.bottomUi.boosters.addChild(s);
            this.sprites.push(s);
            this.scaleUp(s);

            await waitForMs(this.waitTime);

            this.curQuantity--;
        }

        return;
    }

    private scaleUp(s: Sprite) {
        TweenMax.to(s.scale, 0.2, {
            onComplete: () => {
                this.toUi(s);
            },
            x: 1.5,
            y: 1.5,
        });
    }

    private toUi(s: Sprite) {
        TweenMax.to(s.scale, 0.2, {
            delay: 0.5,
            x: 1,
            y: 1,
        });
        TweenMax.to(s, 0.2, {
            delay: 0.5,
            onComplete: () => {
                const less = this.curQuantity * 10;
                this.bottomUi.wallet.updatedCoinTxt(this.addQuantity - less);
                this.bottomUi.boosters.removeChild(s);
                s.destroy();
                this.sprites.splice(this.sprites.indexOf(s), 1);
                this.addStarAnim();
            },
            onStart: () => {
                SoundManager.playSound(this.soundId);
            },
            x: this.targetX,
            y: this.targetY,
        });
    }

    private addStarAnim() {
        const starAnim = new Sparkle(1.5);
        const starAnimSprite = starAnim.sprite;
        starAnimSprite.x = this.targetX;
        starAnimSprite.y = this.targetY;
        starAnimSprite.rotation = 15 * (Math.PI / 180) * -1;
        this.bottomUi.boosters.addChild(starAnimSprite);
        starAnim.play();
    }
}
